import {
  kindFitsSection,
  type SeatKind,
  type SeatingParty,
  type SeatingSection,
  type SeatingState,
  type SeatingTable,
} from '@peles/shared';
import type { SeatingArrangement, SeatingPlacement } from '../../api/seating';

/**
 * The seating board's arithmetic, kept out of the components so it can be
 * tested on its own: who sits at a table (as parties, not chairs), who is
 * still to be seated, what a proposed arrangement would look like before it
 * is applied, and what to put back when a change is undone.
 */

/** A party as it sits at one table: its seats there, in order. */
export interface SeatedParty {
  guestId: string;
  name: string;
  seats: number[];
  /** Any of its seats here is pinned (auto-assign leaves it be). */
  locked: boolean;
}

/** The parties at a table, in the order of their first seat. */
export function partiesAt(table: SeatingTable, parties: Map<string, SeatingParty>): SeatedParty[] {
  const byGuest = new Map<string, SeatedParty>();
  for (const seat of [...table.occupied].sort((a, b) => a.seatNumber - b.seatNumber)) {
    const party = byGuest.get(seat.guestId);
    if (party) {
      party.seats.push(seat.seatNumber);
      party.locked ||= seat.locked;
      continue;
    }
    byGuest.set(seat.guestId, {
      guestId: seat.guestId,
      name: parties.get(seat.guestId)?.name ?? seat.name,
      seats: [seat.seatNumber],
      locked: seat.locked,
    });
  }
  return [...byGuest.values()];
}

export function partyIndex(state: SeatingState): Map<string, SeatingParty> {
  return new Map(state.parties.map((party) => [party.guestId, party]));
}

/** Chairs free at a table within its seat count. */
export function freeSeats(table: SeatingTable): number {
  return Math.max(0, table.seats - table.occupied.length);
}

/** The seat numbers within the table no one holds. */
export function emptySeats(table: SeatingTable): number[] {
  const taken = new Set(table.occupied.map((seat) => seat.seatNumber));
  return Array.from({ length: table.seats }, (_, i) => i + 1).filter((n) => !taken.has(n));
}

/** How many of a party's people still need a seat on this plan. */
export function stillToSeat(party: SeatingParty): number {
  return Math.max(0, party.partySize - party.seatsHeld);
}

export type ResponseFilter = 'coming' | 'all';

/**
 * Parties with people still to seat. "Coming" leaves out those who said no;
 * "all" shows everyone, for a producer seating before the answers are in.
 */
export function unseatedParties(state: SeatingState, filter: ResponseFilter): SeatingParty[] {
  return state.parties.filter(
    (party) => stillToSeat(party) > 0 && (filter === 'all' || party.response !== 'NO'),
  );
}

/** People seated and people expected (everyone but those who said no). */
export function seatedTotals(state: SeatingState): { seated: number; expected: number } {
  let seated = 0;
  let expected = 0;
  for (const party of state.parties) {
    if (party.response === 'NO') continue;
    expected += party.partySize;
    seated += Math.min(party.seatsHeld, party.partySize);
  }
  return { seated, expected };
}

/** The table's label as the board names it. */
export function tableTitle(table: Pick<SeatingTable, 'tableNumber' | 'label'>): string {
  const number = table.tableNumber != null ? `Table ${table.tableNumber}` : null;
  if (number && table.label) return `${number} · ${table.label}`;
  return number ?? table.label ?? 'Table';
}

/**
 * The lowest run of free seats a party fits in, else the lowest free seats,
 * and only then chairs past the table — the server's own rule, so a proposal
 * reads here as it will be applied.
 */
export function pickSeats(taken: Set<number>, size: number, capacity: number): number[] {
  for (let start = 1; start + size - 1 <= capacity; start++) {
    let free = true;
    for (let seat = start; seat < start + size; seat++) {
      if (taken.has(seat)) {
        free = false;
        break;
      }
    }
    if (free) return Array.from({ length: size }, (_, i) => start + i);
  }
  const seats: number[] = [];
  for (let seat = 1; seats.length < size; seat++) {
    if (!taken.has(seat)) seats.push(seat);
  }
  return seats;
}

/**
 * Where a set of parties sit now — what an undo puts back, down to which
 * named person was in which chair.
 */
export function snapshotOf(state: SeatingState, guestIds: string[]): SeatingArrangement {
  const wanted = new Set(guestIds);
  const placements: SeatingPlacement[] = [];
  for (const table of state.tables) {
    const seatsOf = new Map<
      string,
      { seatNumber: number; partyMemberId: string | null; kind?: SeatKind | null }[]
    >();
    for (const seat of table.occupied) {
      if (!wanted.has(seat.guestId)) continue;
      seatsOf.set(seat.guestId, [...(seatsOf.get(seat.guestId) ?? []), seat]);
    }
    for (const [guestId, held] of seatsOf) {
      const sorted = [...held].sort((a, b) => a.seatNumber - b.seatNumber);
      const placement: SeatingPlacement = {
        guestId,
        tableUid: table.uid,
        seats: sorted.map((seat) => seat.seatNumber),
      };
      if (sorted.some((seat) => seat.partyMemberId)) {
        placement.named = sorted.map((seat) => seat.partyMemberId ?? null);
      }
      // Who each seat was given to goes back with it, so an undo keeps a
      // man's seat a man's.
      if (sorted.some((seat) => seat.kind)) {
        placement.kinds = sorted.map((seat) => seat.kind ?? null);
      }
      placements.push(placement);
    }
  }
  return { reseat: [...wanted], placements };
}

/**
 * `size` seats beginning at the chair a producer chose: that one and the free
 * seats after it, then from the table's first seat, then chairs past its end.
 */
export function seatsFrom(taken: Set<number>, start: number, size: number, capacity: number) {
  const seats: number[] = [];
  const add = (seat: number) => {
    if (seats.length < size && !taken.has(seat) && !seats.includes(seat)) seats.push(seat);
  };
  for (let seat = start; seat <= capacity; seat++) add(seat);
  for (let seat = 1; seat < start; seat++) add(seat);
  for (let seat = Math.max(capacity, start) + 1; seats.length < size; seat++) add(seat);
  return seats;
}

/** A proposal as the board shows it, and the seats applying it would take. */
export interface ProjectedProposal {
  state: SeatingState;
  /** Every placement with its seats worked out — what Apply sends. */
  arrangement: SeatingArrangement;
  /** The parties the proposal moves. */
  moved: Set<string>;
}

/**
 * The plan as it would be with a proposal applied: the parties it re-seats
 * give up their unpinned seats, then each placement takes its seats. The
 * conflicts are left out — they are worked out by the server once applied —
 * except a table the proposal fills past its seats, which is plain to see.
 */
export function projectProposal(
  state: SeatingState,
  proposal: SeatingArrangement,
): ProjectedProposal {
  const reseat = new Set(proposal.reseat);
  const parties = partyIndex(state);
  const tables = state.tables.map((table) => ({
    ...table,
    occupied: table.occupied.filter((seat) => seat.locked || !reseat.has(seat.guestId)),
  }));
  const byUid = new Map(tables.map((table) => [table.uid, table]));
  const placements: SeatingPlacement[] = [];
  for (const placement of proposal.placements) {
    const table = byUid.get(placement.tableUid);
    const party = parties.get(placement.guestId);
    if (!table || !party) continue;
    const taken = new Set(table.occupied.map((seat) => seat.seatNumber));
    // As the server does: seats the party still holds (pinned, or not
    // re-seated) stand — a listed one comes off the list — and only what it is
    // still owed is placed.
    const held = tables.reduce(
      (sum, t) => sum + t.occupied.filter((seat) => seat.guestId === party.guestId).length,
      0,
    );
    const holding = new Set(
      table.occupied.filter((seat) => seat.guestId === party.guestId).map((s) => s.seatNumber),
    );
    const listedAt = placement.seats
      ?.map((seat, i) => ({ seat, kind: placement.kinds?.[i] }))
      .filter((entry) => !holding.has(entry.seat));
    const listed = listedAt?.map((entry) => entry.seat);
    const owed = Math.max(0, party.partySize - held);
    const count = Math.min(listed ? listed.length : (placement.count ?? owed), owed);
    if (count <= 0) continue;
    const asked = listed?.slice(0, count);
    const seats =
      asked && asked.every((seat) => !taken.has(seat))
        ? asked
        : placement.startSeat
          ? seatsFrom(taken, placement.startSeat, count, table.seats)
          : pickSeats(taken, count, table.seats);
    table.occupied = [
      ...table.occupied,
      ...seats.map((seatNumber) => ({
        seatNumber,
        guestId: party.guestId,
        partyMemberId: null,
        name: party.name,
        locked: false,
      })),
    ];
    // Who each seat is for stays with it, so a man's seat is still a man's.
    const kinds = placement.kinds
      ? listedAt
        ? listedAt.slice(0, count).map((entry) => entry.kind)
        : placement.kinds.slice(0, count)
      : undefined;
    placements.push({
      guestId: party.guestId,
      tableUid: table.uid,
      seats,
      ...(kinds && kinds.every(Boolean)
        ? { kinds: kinds as NonNullable<typeof placement.kinds> }
        : {}),
    });
  }
  const held = new Map<string, { count: number; tables: string[] }>();
  for (const table of tables) {
    for (const seat of table.occupied) {
      const entry = held.get(seat.guestId) ?? { count: 0, tables: [] };
      entry.count += 1;
      if (!entry.tables.includes(table.uid)) entry.tables.push(table.uid);
      held.set(seat.guestId, entry);
    }
  }
  const projected: SeatingState = {
    ...state,
    tables,
    parties: state.parties.map((party) => ({
      ...party,
      seatsHeld: held.get(party.guestId)?.count ?? 0,
      tableUids: held.get(party.guestId)?.tables ?? [],
    })),
    conflicts: tables
      .filter((table) => table.occupied.length > table.seats)
      .map((table) => ({
        kind: 'OVER_CAPACITY' as const,
        message: `${tableTitle(table)} seats ${table.seats}; the proposal puts ${table.occupied.length} there`,
        guestIds: [...new Set(table.occupied.map((seat) => seat.guestId))],
        tableUid: table.uid,
      })),
  };
  return {
    state: projected,
    arrangement: { reseat: [...reseat], placements },
    moved: new Set(placements.map((p) => p.guestId)),
  };
}

/**
 * A proposal with one party moved by hand: to a table — at the chair chosen,
 * else the lowest free seats there — or off the plan (`null`). The party is
 * re-seated either way.
 */
export function adjustProposal(
  proposal: SeatingArrangement,
  party: Pick<SeatingParty, 'guestId' | 'partySize'>,
  tableUid: string | null,
  seatNumber?: number,
  /** The section of the table it goes to, when known (a mechitza proposal). */
  section?: SeatingSection | null,
): SeatingArrangement {
  const reseat = proposal.reseat.includes(party.guestId)
    ? proposal.reseat
    : [...proposal.reseat, party.guestId];
  const own = proposal.placements.filter((p) => p.guestId === party.guestId);
  const others = proposal.placements.filter((p) => p.guestId !== party.guestId);
  const startSeat = seatNumber ? { startSeat: seatNumber } : {};
  // A party seated by section keeps who each seat is for. Moved to a men's or
  // women's table, only its people of that side move; the rest stay put.
  const sided = own.length > 0 && own.every((p) => p.kinds?.length && p.kinds.every(Boolean));
  if (tableUid && sided && section) {
    const moving = own.filter((p) =>
      p.kinds?.every((kind) => !!kind && kindFitsSection(kind, section)),
    );
    if (moving.length > 0) {
      const kinds = moving.flatMap((p) => p.kinds ?? []);
      return {
        reseat,
        placements: [
          ...proposal.placements.filter((p) => !moving.includes(p)),
          { guestId: party.guestId, tableUid, count: kinds.length, kinds, ...startSeat },
        ],
      };
    }
  }
  if (tableUid) {
    // The whole party moves. Those whose kind is known keep it; anybody not
    // placed yet is a seat nobody recorded, judged by the head count.
    const known = sided ? own.flatMap((p) => p.kinds ?? []) : [];
    const kinds = known.length
      ? [...known, ...Array<null>(Math.max(0, party.partySize - known.length)).fill(null)].slice(
          0,
          party.partySize,
        )
      : [];
    others.push({
      guestId: party.guestId,
      tableUid,
      count: party.partySize,
      ...(kinds.length ? { kinds } : {}),
      ...startSeat,
    });
  }
  return { reseat, placements: others };
}

/**
 * Two parties seated by section trade places side by side: each one's men
 * take the other's men's places and its women the other's women's, so
 * neither table gets both parties' people. Answers null unless both parties'
 * places say who each seat is for (then a swap is two plain moves).
 */
export function swapBySide(
  proposal: SeatingArrangement,
  guestId: string,
  otherId: string,
  sectionAt: (tableUid: string) => SeatingSection | null,
): SeatingArrangement | null {
  const own = (id: string) => proposal.placements.filter((p) => p.guestId === id);
  const sided = (list: SeatingPlacement[]) =>
    list.length > 0 && list.every((p) => p.kinds?.length && p.kinds.every(Boolean));
  const mine = own(guestId);
  const theirs = own(otherId);
  if (!sided(mine) || !sided(theirs)) return null;
  const moveTo = (p: SeatingPlacement, to: SeatingPlacement): SeatingPlacement => ({
    guestId: p.guestId,
    tableUid: to.tableUid,
    count: p.kinds?.length ?? 0,
    kinds: p.kinds,
    ...(to.seats?.[0] ? { startSeat: to.seats[0] } : {}),
  });
  const swapped = new Map<SeatingPlacement, SeatingPlacement>();
  const taken = new Set<SeatingPlacement>();
  for (const p of mine) {
    const side = sectionAt(p.tableUid);
    const q = theirs.find((other) => !taken.has(other) && sectionAt(other.tableUid) === side);
    if (!q) continue;
    taken.add(q);
    swapped.set(p, moveTo(p, q));
    swapped.set(q, moveTo(q, p));
  }
  // No side in common (one at a men's table, the other at a mixed one): the
  // plain swap moves them.
  if (swapped.size === 0) return null;
  return {
    reseat: [...new Set([...proposal.reseat, guestId, otherId])],
    placements: proposal.placements.map((p) => swapped.get(p) ?? p),
  };
}

/**
 * A preview from auto-assign or AI seating read as a proposal. AI answers by
 * table number, so a number is looked up on the plan when it carries no uid.
 */
export function proposalFrom(
  state: SeatingState,
  preview: {
    reseat: string[];
    assignments: Array<{
      guestId: string;
      tableNumber: number;
      tableUid?: string;
      seats?: number[];
      kinds?: SeatingPlacement['kinds'];
    }>;
  },
): SeatingArrangement {
  const uidOf = new Map(
    state.tables.filter((t) => t.tableNumber != null).map((t) => [t.tableNumber, t.uid]),
  );
  const known = new Set(state.tables.map((t) => t.uid));
  const placements: SeatingPlacement[] = [];
  const dropped = new Set<string>();
  for (const a of preview.assignments) {
    const tableUid = a.tableUid ?? uidOf.get(a.tableNumber);
    if (!tableUid || !known.has(tableUid)) {
      dropped.add(a.guestId);
      continue;
    }
    placements.push(
      a.seats
        ? { guestId: a.guestId, tableUid, seats: a.seats, ...(a.kinds ? { kinds: a.kinds } : {}) }
        : { guestId: a.guestId, tableUid },
    );
  }
  // A party sent to a table the plan does not have keeps the seats it holds,
  // rather than being released with nowhere to go.
  const placed = new Set(placements.map((p) => p.guestId));
  const reseat = preview.reseat.filter((id) => !dropped.has(id) || placed.has(id));
  return { reseat, placements };
}
